import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';

import getRadioShow from '../../../api/graphql/queries/getRadioShow';
import postAddRadioShow from '../../../api/graphql/mutations/postAddRadioShow';
import postEditRadioShow from '../../../api/graphql/mutations/postEditRadioShow';
import postDeleteRadioShow from '../../../api/graphql/mutations/postDeleteRadioShow';
import {
  LANGS, REQUIRED_TITLE_LANGS, STATUS_OPTIONS, CHANNEL_OPTIONS, LANG_OPTIONS, SHOW_FIELDS,
  showToFormState, emptyFormState, dirtyFields, buildEditInput, buildCreateInput,
  validateShowForm, groupServerErrors, appleCategoryOptions, appleSubcategoryOptions,
} from '../../../utils/radioShowForm';
import { suggestSlug } from '../../../utils/radioSlug';
import { formatMadrid } from '../../../utils/radioTime';
import Modal from '../../Modal/Modal';
import LoadIcon from '../../LoadIcon';
import LanguageTabsForms from '../../Tabs/LanguageTabsForms';
import RadioRichText from './RadioRichText';
import RadioFileUploader from './RadioFileUploader';
import RadioAiringsField from './RadioAiringsField';
import RadioConfirmModal from './RadioConfirmModal';
import {
  TextField, SelectField, CheckboxField, FieldErrors, portalSelectProps, multilangLabel,
} from './RadioFields';

/*
Create or edit one radio program or episode (they share the form; an episode adds its
program). The rules that matter live in utils/radioShowForm.js:

  - create sends every field; edit sends ONLY what changed, because the backend
    validates only what it is sent and legacy rows must stay editable;
  - the client checks follow the same rule (all fields on create, changed ones on edit);
  - air times are Madrid time in the form and UTC on the wire.

The slug is prefilled on create the way the backend generates it, and follows the title
(and the program) until someone types in it. On edit it is an ordinary field with a
warning, because changing it changes the public URL.

The form owns its Modal so that closing it (X, Escape) can ask about unsaved changes.
*/

const SLUG_FROM_TITLE = Symbol('slug-from-title');

const programLabel = program => `${multilangLabel(program.title, program.slug)} (#${program.id})${program.status === 'oculta' ? ' (hidden)' : ''}`;

const tagOption = tag => ({ value: tag.id, label: multilangLabel(tag.name, `#${tag.id}`) });

const RadioShowForm = ({
  kind: requestedKind, id, initialParent, programs, tags, onClose, onSaved, onDeleted, onAuthFail,
}) => {
  const isEdit = id !== null && id !== undefined;
  const [kind, setKind] = useState(requestedKind);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [show, setShow] = useState(null);
  const [original, setOriginal] = useState(null);
  // A new episode created from a filtered Episodes list starts on that program.
  const [form, setForm] = useState(() => (isEdit ? null : emptyFormState(requestedKind, { parent: initialParent })));
  const [slugMode, setSlugMode] = useState(isEdit ? null : SLUG_FROM_TITLE);
  const [clientErrors, setClientErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({ byField: {}, general: [] });
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyUploads, setBusyUploads] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState([]);
  const createBaseline = useRef(isEdit ? null : emptyFormState(requestedKind, { parent: initialParent }));
  const topRef = useRef(null);
  // `${field}.${lang}` → the editor's flush (see RadioRichText): rich text reaches the form
  // 200 ms late, so Save asks each editor for its current value first.
  const flushers = useRef({});
  const registerFlush = key => (flush) => {
    if (flush) flushers.current[key] = flush;
    else delete flushers.current[key];
  };
  const withFlushedEditors = (current) => {
    let next = current;
    Object.entries(flushers.current).forEach(([key, flush]) => {
      const value = flush();
      const [field, lang] = key.split('.');
      if (value !== undefined && next[field][lang] !== value) {
        next = { ...next, [field]: { ...next[field], [lang]: value } };
      }
    });
    return next;
  };

  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    getRadioShow({ id }).then((result) => {
      if (!active) return;
      if (!result) {
        setLoadError('Could not load this show. Try again.');
      } else if (!result.isAuth) {
        onAuthFail();
        return;
      } else if (!result.show) {
        setLoadError(`Show #${id} was not found (it may have been deleted).`);
      } else {
        const state = showToFormState(result.show);
        setShow(result.show);
        setKind(result.show.parent === 0 ? 'programs' : 'episodes');
        setOriginal(state);
        setForm(state);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [id]);

  const programsById = useMemo(() => programs.reduce((acc, program) => ({ ...acc, [program.id]: program }), {}), [programs]);
  const parentSlug = form && programsById[form.parent] ? programsById[form.parent].slug : '';

  // On create, the slug follows the Spanish title (and the program) until it is typed in.
  useEffect(() => {
    if (!form || slugMode !== SLUG_FROM_TITLE) return;
    const next = suggestSlug(kind, form.title.es, parentSlug);
    if (next !== form.slug) setForm(prev => ({ ...prev, slug: next }));
  }, [form && form.title.es, parentSlug, slugMode, kind]);

  const clearError = (field) => {
    if (clientErrors[field]) {
      setClientErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    clearError(field);
    setNotice('');
  };

  const setLocalized = (field, lang, value) => {
    setForm(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
    clearError(field);
    setNotice('');
  };

  const changedFields = form && original ? dirtyFields(original, form) : [];
  const hasUnsavedChanges = isEdit
    ? changedFields.length > 0
    : Boolean(form && dirtyFields(createBaseline.current, form).some(field => field !== 'slug'));
  const uploading = Object.values(busyUploads).some(Boolean);

  const requestClose = () => {
    if (saving || deleting) return;
    // eslint-disable-next-line no-alert
    if (uploading && !window.confirm('An upload is still running. Close and cancel it?')) return;
    // eslint-disable-next-line no-alert
    if (!uploading && hasUnsavedChanges && !window.confirm('Close without saving your changes?')) return;
    onClose();
  };

  const scrollToTop = () => {
    if (topRef.current && topRef.current.scrollIntoView) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || uploading) return;
    const flushedForm = withFlushedEditors(form);
    const fields = isEdit ? dirtyFields(original, flushedForm) : SHOW_FIELDS;
    if (isEdit && fields.length === 0) {
      setNotice('Nothing has changed.');
      return;
    }
    const errors = validateShowForm(flushedForm, { kind, fields, isEdit });
    setClientErrors(errors);
    setServerErrors({ byField: {}, general: [] });
    if (Object.keys(errors).length) {
      setNotice('');
      scrollToTop();
      return;
    }

    setSaving(true);
    const result = isEdit
      ? await postEditRadioShow({ id, input: buildEditInput(original, flushedForm) })
      : await postAddRadioShow({ input: buildCreateInput(flushedForm, kind) });
    setSaving(false);

    if (!result) {
      setServerErrors({ byField: {}, general: ['The server did not answer. Nothing was saved; try again.'] });
      scrollToTop();
      return;
    }
    if (!result.isAuth) {
      onAuthFail();
      return;
    }
    if (!result.ok) {
      const grouped = groupServerErrors(result.errors);
      if (!grouped.general.length && !Object.keys(grouped.byField).length) grouped.general.push('The show was not saved.');
      setServerErrors(grouped);
      scrollToTop();
      return;
    }
    onSaved(result.show, { created: !isEdit, kind });
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteErrors([]);
    const result = await postDeleteRadioShow({ id });
    setDeleting(false);
    if (!result) {
      setDeleteErrors(['The server did not answer. Nothing was deleted.']);
      return;
    }
    if (!result.isAuth) {
      onAuthFail();
      return;
    }
    if (!result.ok) {
      const messages = (result.errors || []).map(error => error && error.message).filter(Boolean);
      setDeleteErrors(messages.length ? messages : ['The show was not deleted.']);
      return;
    }
    setConfirmDelete(false);
    onDeleted(id, { kind });
  };

  // Client messages first; the backend's for the same field after them. Row-level
  // airing errors from the backend ('airings.2.date') go next to their row too.
  const errorFor = field => [
    ...[].concat(clientErrors[field] || []),
    ...(serverErrors.byField[field] || []),
  ];
  const airingErrors = useMemo(() => {
    const merged = { ...clientErrors };
    const serverAirings = serverErrors.byField.airings || [];
    if (serverAirings.length) merged.airings = [...[].concat(clientErrors.airings || []), ...serverAirings];
    return merged;
  }, [clientErrors, serverErrors]);

  const allMessages = [
    ...serverErrors.general,
    ...Object.entries(serverErrors.byField).map(([field, messages]) => `${field}: ${messages.join(' ')}`),
    ...Object.entries(clientErrors).filter(([field]) => !field.startsWith('airings.')).map(([, message]) => message),
  ];

  const title = isEdit
    ? `Edit ${kind === 'programs' ? 'program' : 'episode'} #${id}`
    : `New ${kind === 'programs' ? 'program' : 'episode'}`;

  const renderBody = () => {
    if (loading) return <LoadIcon />;
    if (loadError) {
      return (
        <div className="radio-form">
          <p className="radio-error-text">{loadError}</p>
          <div className="radio-buttons">
            <button type="button" className="radio-button" onClick={onClose}>Close</button>
          </div>
        </div>
      );
    }

    const parentOptions = programs.map(program => ({ value: String(program.id), label: programLabel(program) }));
    if (kind === 'episodes' && form.parent && form.parent !== '0' && !programsById[form.parent]) {
      parentOptions.push({ value: form.parent, label: `#${form.parent} (program missing or deleted)` });
    }
    const tagOptions = tags.filter(tag => !tag.isCategory).map(tagOption);
    const categoryOptions = tags.filter(tag => tag.isCategory).map(tagOption);
    const pickedOptions = (ids, options) => ids.map(tagId => options.find(option => option.value === tagId)
      || { value: tagId, label: `#${tagId} (unknown tag)` });

    return (
      <form className="radio-form font-americaMonoRegular" onSubmit={handleSubmit} noValidate>
        <div ref={topRef} className="radio-form-title font-americaMonoBold">
          {title}
          {show && show.slug ? <span className="radio-help">{` · ${show.slug}`}</span> : null}
        </div>

        {allMessages.length ? (
          <div className="radio-errors-box">
            <strong>Not saved.</strong>
            <ul>
              {allMessages.map(message => <li key={message}>{message}</li>)}
            </ul>
          </div>
        ) : null}

        {kind === 'episodes' ? (
          <div className="radio-stack" style={{ marginBottom: 16 }}>
            <div className="flex flex-col text-xs uppercase">
              Program
              <ReactSelect
                inputId="radio-parent"
                aria-label="Program"
                className="text-black mt-2 normal-case"
                options={parentOptions}
                value={parentOptions.find(option => option.value === form.parent) || null}
                onChange={option => setField('parent', option ? option.value : '')}
                placeholder="Choose the program…"
                {...portalSelectProps()}
              />
            </div>
            <FieldErrors error={errorFor('parent')} />
          </div>
        ) : null}

        <LanguageTabsForms activeTabStyle="tab active" width="w-full" languages={LANGS} defaultLanguage="es">
          {activeLanguage => (
            <div className="radio-lang-panel">
              {LANGS.map(lang => (
                <div key={lang} className={activeLanguage === lang ? 'radio-stack' : 'hidden'}>
                  <TextField
                    name={`radio-title-${lang}`}
                    label={`Title (${lang})${REQUIRED_TITLE_LANGS.includes(lang) ? ' *' : ''}`}
                    value={form.title[lang]}
                    onChange={value => setLocalized('title', lang, value)}
                    error={errorFor('title')}
                  />
                  <div className="text-xs uppercase">
                    {`Subtitle (${lang}) — bold and italic only`}
                    <RadioRichText
                      id={`radio-subtitle-${lang}`}
                      variant="inline"
                      registerFlush={registerFlush(`subtitle.${lang}`)}
                      value={form.subtitle[lang]}
                      onChange={value => setLocalized('subtitle', lang, value)}
                    />
                    <FieldErrors error={errorFor('subtitle')} />
                  </div>
                  <div className="text-xs uppercase">
                    {`Text (${lang})`}
                    <RadioRichText
                      id={`radio-text-${lang}`}
                      registerFlush={registerFlush(`text.${lang}`)}
                      value={form.text[lang]}
                      onChange={value => setLocalized('text', lang, value)}
                    />
                    <FieldErrors error={errorFor('text')} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </LanguageTabsForms>

        <div className="radio-section-title">URL</div>
        <TextField
          name="radio-slug"
          label="Slug"
          value={form.slug}
          onChange={(value) => {
            setSlugMode(null);
            setField('slug', value);
          }}
          error={errorFor('slug')}
          placeholder={kind === 'episodes' && !parentSlug ? 'Choose the program first' : ''}
          help={isEdit ? null : (
            <>
              {kind === 'episodes'
                ? 'Generated as <program slug>-<Spanish title>, the way the backend will. You can change it before saving.'
                : 'Generated from the Spanish title, the way the backend will. You can change it before saving.'}
              {slugMode !== SLUG_FROM_TITLE ? (
                <>
                  {' '}
                  <button type="button" className="radio-link" onClick={() => setSlugMode(SLUG_FROM_TITLE)}>Generate it again</button>
                </>
              ) : null}
            </>
          )}
        />
        {isEdit ? (
          <p className="radio-warning">
            Changing the slug changes the public URL: the old link stops working on the website, in the podcast RSS and
            anywhere it was shared.
            {kind === 'programs' ? ' Episode slugs start with the program slug and do not change with it.' : ''}
          </p>
        ) : null}

        <div className="radio-section-title">Publishing</div>
        <div className="radio-grid">
          <SelectField name="radio-status" label="Status" value={form.status} options={STATUS_OPTIONS} onChange={value => setField('status', value)} error={errorFor('status')} />
          <SelectField name="radio-channel" label="Channel" value={form.channel} options={CHANNEL_OPTIONS} onChange={value => setField('channel', value)} error={errorFor('channel')} />
          <SelectField name="radio-lang" label="Language" value={form.lang} options={LANG_OPTIONS} onChange={value => setField('lang', value)} error={errorFor('lang')} />
          <TextField name="radio-duration" label="Duration (M:SS)" placeholder="58:30" value={form.duration} onChange={value => setField('duration', value)} error={errorFor('duration')} />
          <CheckboxField name="radio-highlight" label="Highlight" checked={form.highlight} onChange={value => setField('highlight', value)} />
          <CheckboxField name="radio-is-playlist" label="Is a playlist" checked={form.isPlaylist} onChange={value => setField('isPlaylist', value)} />
        </div>

        <div className="radio-section-title">Image and audio</div>
        <div className="radio-grid-2">
          <RadioFileUploader
            kind="image"
            name="radio-image"
            label="Image"
            value={form.image}
            onChange={value => setField('image', value)}
            onBusyChange={busy => setBusyUploads(prev => ({ ...prev, image: busy }))}
            help="At least 1200×627, aspect ratio 1.91."
            error={errorFor('image')}
          />
          <div className="radio-stack">
            <RadioFileUploader
              kind="mp3"
              name="radio-mp3"
              label="MP3"
              value={form.mp3}
              onChange={value => setField('mp3', value)}
              onUploaded={({ fileSize }) => setField('fileSize', String(fileSize))}
              onBusyChange={busy => setBusyUploads(prev => ({ ...prev, mp3: busy }))}
              error={errorFor('mp3')}
            />
            <TextField
              name="radio-file-size"
              label="MP3 size (bytes)"
              value={form.fileSize}
              onChange={value => setField('fileSize', value)}
              error={errorFor('fileSize')}
              help="Filled in by the upload. The podcast feed needs it; set it by hand only for a pasted URL."
            />
          </div>
        </div>
        <div className="radio-grid-2" style={{ marginTop: 16 }}>
          <TextField name="radio-image-square" label="Square image URL" value={form.imageSquare} onChange={value => setField('imageSquare', value)} error={errorFor('imageSquare')} />
        </div>

        <div className="radio-section-title">Links</div>
        <div className="radio-grid">
          <TextField name="radio-mixcloud" label="Mixcloud" value={form.mixcloud} onChange={value => setField('mixcloud', value)} error={errorFor('mixcloud')} help="The path, e.g. /RadioPrimaveraSound/the-show-name/" />
          <TextField name="radio-youtube" label="YouTube" value={form.youtube} onChange={value => setField('youtube', value)} error={errorFor('youtube')} help="The video id only." />
          <TextField name="radio-spotify" label="Spotify" value={form.spotify} onChange={value => setField('spotify', value)} error={errorFor('spotify')} />
        </div>

        <div className="radio-section-title">Apple Podcasts</div>
        <div className="radio-grid">
          <SelectField
            name="radio-apple-category"
            label="Category"
            value={form.appleCategory}
            options={[{ value: '', label: '—' }, ...appleCategoryOptions(form.appleCategory).map(name => ({ value: name, label: name }))]}
            onChange={(value) => {
              setField('appleCategory', value);
              if (!appleSubcategoryOptions(value, '').includes(form.appleSubcategory)) setField('appleSubcategory', '');
            }}
            error={errorFor('appleCategory')}
          />
          <SelectField
            name="radio-apple-subcategory"
            label="Subcategory"
            value={form.appleSubcategory}
            options={[{ value: '', label: '—' }, ...appleSubcategoryOptions(form.appleCategory, form.appleSubcategory).map(name => ({ value: name, label: name }))]}
            onChange={value => setField('appleSubcategory', value)}
            error={errorFor('appleSubcategory')}
          />
        </div>

        <div className="radio-section-title">Categories and tags</div>
        <div className="radio-grid-2">
          <div className="flex flex-col text-xs uppercase">
            Categories
            <ReactSelect
              inputId="radio-categories"
              aria-label="Categories"
              isMulti
              className="text-black mt-2 normal-case"
              options={categoryOptions}
              value={pickedOptions(form.categoryIds, categoryOptions)}
              onChange={options => setField('categoryIds', (options || []).map(option => option.value))}
              {...portalSelectProps()}
            />
            <FieldErrors error={errorFor('categoryIds')} />
          </div>
          <div className="flex flex-col text-xs uppercase">
            Tags
            <ReactSelect
              inputId="radio-tags"
              aria-label="Tags"
              isMulti
              className="text-black mt-2 normal-case"
              options={tagOptions}
              value={pickedOptions(form.tagIds, tagOptions)}
              onChange={options => setField('tagIds', (options || []).map(option => option.value))}
              {...portalSelectProps()}
            />
            <FieldErrors error={errorFor('tagIds')} />
          </div>
        </div>

        <div className="radio-section-title">Air times</div>
        <RadioAiringsField
          rows={form.airings}
          onChange={rows => setField('airings', rows)}
          errors={airingErrors}
          defaultChannel={form.channel}
        />

        {show ? (
          <p className="radio-help" style={{ marginTop: 20 }}>
            {`Created ${formatMadrid(show.createdAt) || '—'} · Updated ${formatMadrid(show.updatedAt) || '—'} (Madrid time)`}
          </p>
        ) : null}

        <div className="radio-buttons">
          {notice ? <span className="radio-notice">{notice}</span> : null}
          {uploading ? <span className="radio-notice">Wait for the upload to finish.</span> : null}
          {isEdit ? (
            <button
              type="button"
              className="radio-button is-danger"
              onClick={() => {
                setDeleteErrors([]);
                setConfirmDelete(true);
              }}
              disabled={saving}
            >
              Delete
            </button>
          ) : null}
          <button type="button" className="radio-button is-ghost" onClick={requestClose} disabled={saving}>Cancel</button>
          <button type="submit" className="radio-button" disabled={saving || uploading}>
            {(() => {
              if (saving) return 'Saving…';
              return isEdit ? 'Save' : 'Create';
            })()}
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <Modal handleClick={requestClose} type="radio-show">
        {renderBody()}
      </Modal>
      {confirmDelete ? (
        <RadioConfirmModal
          title={`Delete ${kind === 'programs' ? 'program' : 'episode'} #${id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          busy={deleting}
          errors={deleteErrors}
        >
          <p>{`Delete “${multilangLabel(show && show.title, show ? show.slug : '')}”?`}</p>
          <p>
            {kind === 'programs'
              ? 'A program that still has episodes cannot be deleted: delete or move its episodes first.'
              : 'It disappears from the website, the schedule and the podcast feed.'}
          </p>
        </RadioConfirmModal>
      ) : null}
    </>
  );
};

RadioShowForm.propTypes = {
  kind: PropTypes.oneOf(['programs', 'episodes']).isRequired,
  // null to create
  id: PropTypes.number,
  // create only: the program a new episode starts on
  initialParent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    title: PropTypes.shape({}),
    status: PropTypes.string,
  })),
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.shape({}),
    isCategory: PropTypes.bool,
  })),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
  onAuthFail: PropTypes.func.isRequired,
};

RadioShowForm.defaultProps = {
  id: null,
  initialParent: '',
  programs: [],
  tags: [],
};

export default RadioShowForm;
